import { useEffect, useState, useSyncExternalStore } from 'react'
import * as signalR from '@microsoft/signalr'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../store/authStore'
import type { DeviceSpecDetail } from '../../types'
import { newKey } from './model'
import { recomputeDevices } from './recompute'
import { applyOps, changeId, diff, merge, opsOf, order, toDraft } from './specDoc'
import type { Change, Draft, SpecOp } from './specDoc'

export type { Draft } from './specDoc'
export { hasAddress, toDraft } from './specDoc'

// 명세서 실시간 공동 편집 (SpecHub, /hubs/spec) — 일반 화면과 편집 모드가 같이 쓴다. 네트워크 도면(netSync)과 같은 방식:
//  · 확정 상태(confirmed) = 서버가 방 전체에 보낸 변경(Ops)을 받은 순서대로 누적한 것 — 모두 같은 순서로 받으므로 모두 같다.
//  · 화면 = 확정 상태 + 아직 서버가 확인 안 한 내 변경(pending: 보냄, outbox: 모으는 중), 그 위에 수식 다시 계산.
//  · 변경은 항목의 바뀐 칸만 보낸다 (specDoc) — 같은 칸을 동시에 고치면 서버에 나중에 도착한 쪽이 남는다.
//  · 되돌리기는 내가 바꾼 칸만 이전 값으로 (남이 바꾼 칸은 건드리지 않음). 0.8초 안에 이어지는 변경은 한 번으로 친다.
//  · 글자 입력처럼 잦은 변경은 0.25초 모아서 보낸다. 자동 저장 = 보내면 저장.
//  · 주소 없는 태그(작성 중인 행)도 공동 편집에는 남는다 — Edge 적용·목록 개수에서는 빠진다 (hasAddress).

// ── 연결 ──

// mode: sheet(디바이스 시트) | tags(태그 편집) | panel(설정 서랍) | machines(인터페이스 시트) | items(항목 편집)
export type SpecCursor = { mode: 'sheet' | 'tags' | 'panel' | 'machines' | 'items'; device: string | null; tag?: string | null; field?: string | null; editing?: boolean }
export type SpecViewer = { connectionId: string; userId: string | null; name: string; color: number; cursor: SpecCursor | null }
export type SaveStatus = 'saved' | 'saving' | 'connecting' | 'reconnecting' | 'error'

const FLUSH_MS = 250
const COALESCE_MS = 800
const HISTORY_LIMIT = 100

type Batch = { id: string; ops: SpecOp[] }
type Changeset = { at: number; items: Map<string, Change> }

type Snapshot = {
  draft: Draft
  status: SaveStatus
  error: string | null
  viewers: SpecViewer[]
  me: string | null
  canUndo: boolean
  canRedo: boolean
  savedAt: Date
  deleted: boolean
}

class SpecSync {
  private conn: signalR.HubConnection | null = null
  private joining = false
  private ready = false
  private buffered: { batchId: string; ops: SpecOp[]; revision: number }[] = []
  private confirmed: Draft
  private rev: number
  private pending: Batch[] = []
  private outbox: SpecOp[] = []
  private past: Changeset[] = []
  private future: Changeset[] = []
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private listeners = new Set<() => void>()
  private snap: Snapshot
  private disposed = false
  private cursor: SpecCursor | null = null
  private specId: string
  private onSaved: () => void

  constructor(initial: DeviceSpecDetail, onSaved: () => void) {
    this.specId = initial.id
    this.confirmed = toDraft(initial)
    this.rev = initial.revision
    this.onSaved = onSaved
    this.snap = {
      draft: this.view(), status: 'connecting', error: null, viewers: [], me: null,
      canUndo: false, canRedo: false, savedAt: new Date(initial.updatedAt), deleted: false,
    }
  }

  private get connected() { return this.conn?.state === signalR.HubConnectionState.Connected }

  start() {
    this.disposed = false
    const conn = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/spec', { accessTokenFactory: () => useAuthStore.getState().accessToken ?? '' })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()
    conn.on('SpecOps', (batchId: string, ops: SpecOp[], _by: string, revision: number) => this.receive(batchId, ops, revision))
    conn.on('SpecPresence', (viewers: SpecViewer[]) => this.set({ viewers }))
    conn.on('SpecDeleted', () => {
      this.outbox = []
      this.pending = []
      this.set({ deleted: true, error: '다른 사용자가 이 명세서를 삭제했습니다.' })
      this.publish()
    })
    conn.onreconnecting(() => { this.ready = false; this.publish() })
    conn.onreconnected(() => this.join())
    conn.onclose(() => { if (!this.disposed && this.conn === conn) this.set({ error: '실시간 연결이 끊겼습니다. 새로고침하세요.' }); this.publish() })
    this.conn = conn
    conn.start().then(() => { if (this.conn === conn) this.join() })
      .catch(err => { if (this.conn === conn) { this.set({ error: messageOf(err, '실시간 연결에 실패했습니다.') }); this.publish() } })
  }

  dispose() {
    this.disposed = true
    this.flush()
    const conn = this.conn
    this.conn = null
    // 남은 변경이 서버에 닿을 시간을 조금 준 뒤 닫는다
    setTimeout(() => conn?.stop(), this.pending.length ? 1500 : 0)
  }

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn) } }
  getSnapshot = () => this.snap

  private async join() {
    if (!this.conn || this.joining) return
    this.joining = true
    try {
      const r = await this.conn.invoke<{ connectionId: string; spec: DeviceSpecDetail; viewers: SpecViewer[] }>('Join', this.specId)
      this.confirmed = toDraft(r.spec)
      this.rev = r.spec.revision
      this.joining = false
      this.ready = true
      // 들어오는 동안 도착한 변경 — 원본에 이미 든 것(revision 이하)은 건너뛴다
      const buffered = this.buffered
      this.buffered = []
      for (const b of buffered) this.receive(b.batchId, b.ops, b.revision)
      this.set({ me: r.connectionId, viewers: r.viewers, error: null })
      // 재접속이면 확인 못 받은 변경을 다시 보낸다 (같은 batchId — 칸 값을 넣는 변경이라 두 번 가도 같다)
      for (const b of this.pending) this.send(b)
      if (this.cursor) this.conn.invoke('Locate', this.specId, this.cursor).catch(() => {})
      this.flush()
      this.publish()
    } catch (err) {
      this.joining = false
      this.set({ error: messageOf(err, '명세서에 들어가지 못했습니다.') })
      this.publish()
    }
  }

  private receive(batchId: string, ops: SpecOp[], revision: number) {
    if (this.joining) { this.buffered.push({ batchId, ops, revision }); return }
    const mine = this.pending.some(b => b.id === batchId)
    if (revision <= this.rev) { this.pending = this.pending.filter(b => b.id !== batchId); this.publish(); return }
    if (revision !== this.rev + 1) { this.join(); return } // 놓친 변경이 있다 — 원본을 다시 받는다
    this.rev = revision
    this.confirmed = applyOps(this.confirmed, ops)
    this.pending = this.pending.filter(b => b.id !== batchId)
    this.snap = { ...this.snap, savedAt: new Date() }
    if (mine) this.onSaved()
    this.publish()
  }

  // ── 내 변경 ──

  update(fn: (d: Draft) => Draft) {
    if (this.snap.deleted) return
    const view = this.view()
    const updated = fn(view)
    if (updated === view) return
    // 수식 칸 다시 계산 — 설비구분·디바이스 코드나 위 칸이 바뀌면 값이 따라 바뀐다 (그 값도 같이 보낸다)
    const changes = diff(view, { ...updated, devices: recomputeDevices(updated.devices) })
    if (changes.length === 0) return
    this.record(changes)
    this.queue(changes.flatMap(c => opsOf(c, 'after')))
  }

  undo() { this.travel(this.past, this.future, 'before') }
  redo() { this.travel(this.future, this.past, 'after') }

  private travel(from: Changeset[], to: Changeset[], side: 'before' | 'after') {
    const cs = from.pop()
    if (!cs) return
    to.push(cs)
    // 되돌릴 때는 지운 디바이스를 먼저 살린 뒤 태그를, 만든 디바이스는 마지막에 지운다. 같은 종류끼리는 원래 순서 —
    // 지운 태그를 살릴 때 앞 태그부터 놓아야 "그 뒤" 자리가 맞는다
    const items = [...cs.items.values()]
    this.queue((side === 'before' ? items.sort((x, y) => order(x) - order(y)) : items).flatMap(c => opsOf(c, side)))
  }

  private record(changes: Change[]) {
    const now = Date.now()
    const last = this.past[this.past.length - 1]
    if (last && now - last.at < COALESCE_MS) {
      for (const c of changes) {
        const prev = last.items.get(changeId(c))
        last.items.set(changeId(c), prev ? merge(prev, c) : c)
      }
      last.at = now
    } else {
      this.past.push({ at: now, items: new Map(changes.map(c => [changeId(c), c])) })
      if (this.past.length > HISTORY_LIMIT) this.past.shift()
    }
    this.future = []
  }

  private queue(ops: SpecOp[]) {
    this.outbox.push(...ops)
    if (this.flushTimer) clearTimeout(this.flushTimer)
    this.flushTimer = setTimeout(() => this.flush(), FLUSH_MS)
    this.publish()
  }

  flush() {
    if (this.flushTimer) { clearTimeout(this.flushTimer); this.flushTimer = null }
    if (this.outbox.length === 0 || !this.connected || !this.ready || this.joining) return
    const batch = { id: newKey(), ops: this.outbox }
    this.outbox = []
    this.pending.push(batch)
    this.send(batch)
    this.publish()
  }

  private send(batch: Batch) {
    this.conn?.invoke('Apply', this.specId, batch.id, batch.ops).catch(err => {
      // 거절된 변경은 버리고 화면을 서버 상태로 되돌린다
      this.pending = this.pending.filter(b => b.id !== batch.id)
      this.set({ error: messageOf(err, '변경을 저장하지 못했습니다.') })
      this.publish()
    })
  }

  /** 끊겼거나 실패했을 때 다시 연결 */
  retry() {
    if (this.snap.deleted) return
    this.set({ error: null })
    if (this.connected) { this.join(); return }
    this.conn?.stop().finally(() => this.start())
  }

  // ── 표시 전용 ──

  locate(cursor: SpecCursor | null) {
    if (JSON.stringify(cursor) === JSON.stringify(this.cursor)) return
    this.cursor = cursor
    if (this.connected && this.ready) this.conn!.invoke('Locate', this.specId, cursor).catch(() => {})
  }

  // ── 상태 ──

  private view(): Draft {
    const d = applyOps(applyOps(this.confirmed, this.pending.flatMap(b => b.ops)), this.outbox)
    return { ...d, devices: recomputeDevices(d.devices) }
  }

  private publish() {
    const busy = this.pending.length > 0 || this.outbox.length > 0
    const status: SaveStatus = this.snap.error || this.snap.deleted ? 'error'
      : this.ready ? (busy ? 'saving' : 'saved')
        : this.snap.me ? 'reconnecting' : 'connecting'
    this.set({ draft: this.view(), status, canUndo: this.past.length > 0, canRedo: this.future.length > 0 })
  }

  private set(patch: Partial<Snapshot>) {
    this.snap = { ...this.snap, ...patch }
    for (const fn of this.listeners) fn()
  }
}

function messageOf(err: unknown, fallback: string) {
  const m = err instanceof Error ? err.message : ''
  // HubException 메시지는 "... HubException: 내용" 꼴
  const hub = /HubException: (.+)$/.exec(m)
  return hub ? hub[1] : fallback
}

/** 명세서 하나의 공동 편집 연결 */
export function useSpecDraft(initial: DeviceSpecDetail) {
  const qc = useQueryClient()
  const [sync] = useState(() => new SpecSync(initial, () => {
    // 목록(디바이스·태그 수, 수정 시각)과 상세 캐시는 다음에 볼 때 새로
    qc.invalidateQueries({ queryKey: ['device-specs'], exact: true })
  }))
  // 함수는 늘 같은 것 — 효과(useEffect) 의존성에 넣어도 다시 돌지 않게
  const [api] = useState(() => ({
    setDraft: (update: (d: Draft) => Draft) => sync.update(update),
    undo: () => sync.undo(),
    redo: () => sync.redo(),
    saveNow: () => (sync.getSnapshot().error ? sync.retry() : sync.flush()),
    locate: (cursor: SpecCursor | null) => sync.locate(cursor),
  }))
  useEffect(() => {
    sync.start()
    return () => sync.dispose()
  }, [sync])
  const snap = useSyncExternalStore(sync.subscribe, sync.getSnapshot)
  return {
    ...api,
    draft: snap.draft,
    status: snap.status,
    savedAt: snap.savedAt,
    errorMessage: snap.error,
    deleted: snap.deleted,
    viewers: snap.viewers,
    me: snap.me,
    canUndo: snap.canUndo,
    canRedo: snap.canRedo,
  }
}

export type SpecDraft = ReturnType<typeof useSpecDraft>

export const STATUS_TEXT: Record<SaveStatus, string> = {
  saved: '자동 저장됨',
  saving: '저장 중…',
  connecting: '연결 중…',
  reconnecting: '다시 연결 중…',
  error: '저장 안 됨',
}
